/**
 * List collection list provider.
 *
 * Mirrors `FoodCollectionList` semantics for list collections.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, QueryRowType, PromiseWithServerResult } from "@rocicorp/zero";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import { ListCollection } from "@corpuscomplete/core/common/list-collection";

type RootManagementRow = QueryRowType<typeof queries.listCollection.forRootManagement>;

export namespace ListCollectionList {
  export type Context = {
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    userId: string;
  }>;

  export function DataProvider({ children, userId }: DataProviderProps) {
    return <DataContext value={{ userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "ListCollectionList.useContext must be used within ListCollectionList.DataProvider",
      );
    }
    return context;
  }

  export type CollectionPreview = RootManagementRow & {
    role: AccessGrant.Role;
    isDefault: boolean;
    listCount: number;
  };

  export function useCollections(): CollectionPreview[] {
    const [collections] = useQuery(queries.listCollection.forRootManagement({ limit: 50 }));

    return useMemo(
      () =>
        collections.map((c) => ({
          ...c,
          role: AccessGrant.highestRole(c.grants),
          isDefault: c.defaultForUserId !== null,
          listCount: c.lists.length,
        })),
      [collections],
    );
  }

  export function useDefaultCollection(): CollectionPreview | undefined {
    const collections = useCollections();
    return useMemo(() => collections.find((c) => c.isDefault), [collections]);
  }

  export function useLoadingState(): { isLoading: boolean } {
    const [, details] = useQuery(queries.listCollection.forRootManagement({ limit: 50 }));
    return {
      isLoading: details.type === "unknown",
    };
  }

  export type Mutations = {
    create: (params: { id: string; name: string }) => PromiseWithServerResult;
    rename: (id: string, name: string) => PromiseWithServerResult;
    del: (id: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();

    return useMemo(
      () => ({
        create: ({ id, name }) => z.mutate(mutators.listCollection.create({ id, name })),
        rename: (id, name) => z.mutate(mutators.listCollection.rename({ id, name })),
        del: (id) => z.mutate(mutators.listCollection.delete({ id })),
      }),
      [z],
    );
  }

  export function preload(zero: Zero): void {
    void zero.run(queries.listCollection.forRootManagement({ limit: 50 }));
    void zero.run(queries.listCollection.myDefault());
  }

  export type CollectionPermissions = {
    canRename: boolean;
    canDelete: boolean;
  };

  export function useCollectionPermissions(): (
    collection: CollectionPreview,
  ) => CollectionPermissions {
    const [grants] = useQuery(queries.accessGrant.myGrants());

    return useMemo(() => {
      return (collection: CollectionPreview) => {
        const perms = AccessGrant.checkPermissions(
          grants,
          ListCollection.Permissions,
          collection.id,
        );
        return {
          canRename: perms.canRename && !collection.isDefault,
          canDelete: perms.canDelete && !collection.isDefault && collection.listCount === 0,
        };
      };
    }, [grants]);
  }
}
